import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  STEP_LABELS,
  VISIBLE_STEPS,
  type OnboardingStep,
} from "@/api/onboarding-api";
import { SPRING } from "./motion";

/**
 * The header rail: where the user is, what's behind them, what's left.
 *
 * Completion is drawn rather than toggled — the connector fills, the dot
 * inflates, then the tick strokes itself in — so finishing a step gets a small
 * moment of its own instead of a silent state change.
 */
export function ProgressRail({
  currentStep,
  completedSteps,
}: {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
}) {
  const shouldReduce = useReducedMotion();
  const currentIndex = VISIBLE_STEPS.indexOf(currentStep as never);

  return (
    <div className="flex items-center gap-1.5 sm:gap-2.5">
      {VISIBLE_STEPS.map((step, i) => {
        const isDone = completedSteps.includes(step);
        const isCurrent = step === currentStep;
        const isPast = currentIndex > i;

        return (
          <div key={step} className="flex items-center gap-1.5 sm:gap-2.5">
            {i > 0 && (
              <div className="relative h-px w-4 overflow-hidden rounded-full bg-border sm:w-7">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-primary"
                  initial={{ width: "0%" }}
                  animate={{ width: isDone || isPast ? "100%" : "0%" }}
                  transition={shouldReduce ? { duration: 0 } : { duration: 0.4, ease: "easeOut" }}
                />
              </div>
            )}

            <div className="flex items-center gap-1.5">
              <motion.div
                className={cn(
                  "relative flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold",
                  isDone
                    ? "bg-primary text-primary-foreground"
                    : isCurrent
                      ? "bg-primary/12 text-primary ring-2 ring-primary/30"
                      : "bg-muted text-muted-foreground/60"
                )}
                animate={{ scale: 1 }}
                transition={SPRING}
                layout
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isDone ? (
                    <motion.span
                      key="check"
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0 }}
                      transition={SPRING}
                    >
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="num"
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.6, opacity: 0 }}
                    >
                      {i + 1}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Only the active label shows on narrow screens — the dots
                  carry the position, the label carries the meaning. */}
              <span
                className={cn(
                  "text-xs font-medium transition-colors",
                  isCurrent
                    ? "text-foreground"
                    : "hidden text-muted-foreground/70 sm:inline",
                  isDone && "text-muted-foreground"
                )}
              >
                {STEP_LABELS[step]}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
